import { supabase } from '../supabaseClient'
import { nombreSeguroStorage } from './nombre-storage'
import { jsPDF } from 'jspdf'
import * as XLSX from 'xlsx'
import { ProcessWithHierarchy, JobPositionLegalFields, TaskHazardAssessment } from './gestion-tecnica-constructor'
import { CATEGORIAS_PELIGRO } from './iperCalculations'

export async function fetchHazardsForTasks(taskIds: string[]): Promise<TaskHazardAssessment[]> {
  if (taskIds.length === 0) return []
  const { data, error } = await supabase
    .from('iper_assessments')
    .select('id, task_id, hazard_category, hazard_type, hazard_description, possible_effects, control_source, control_medium, control_individual, nivel_deficiencia, nivel_exposicion, nivel_probabilidad, interpretacion_probabilidad, nivel_consecuencia, nivel_riesgo, interpretacion_riesgo, aceptabilidad, medida_eliminacion, medida_sustitucion, medida_control_ingenieria, medida_control_administrativo, medida_epp')
    .in('task_id', taskIds)
    .order('created_at')

  if (error) throw error
  return (data ?? []) as TaskHazardAssessment[]
}

export interface PdfHelpers {
  doc: jsPDF
  txt: (str: string, fs: number, style?: 'normal' | 'bold' | 'italic', align?: 'left' | 'center' | 'right', indent?: number, gap?: number) => void
  skip: (mm: number) => void
  hline: () => void
  // Expuestos para módulos que necesitan dibujar contenido propio (ej. la
  // planta de Iluminación) intercalado con el flujo normal de texto — leen
  // la posición vertical actual y reservan espacio con el mismo control de
  // salto de página que usa txt() internamente.
  getY: () => number
  checkBreak: (needed: number) => void
  drawTable: (config: {
    title: string
    colHeaders: string[]
    rowHeaders: string[]
    data: number[][]
    highlightRow: number
    highlightCol: number
  }) => void
}

export const PDF_MARGIN_MM = 25 // margen de página usado por createPdfHelpers() (M)
export const PDF_PAGE_WIDTH_MM = 210 // PW — ancho de página A4 en mm, compartido entre createPdfHelpers() y generateFlowDiagramPDF()
export const PDF_CONTENT_WIDTH_MM = PDF_PAGE_WIDTH_MM - PDF_MARGIN_MM * 2 // CW

export function createPdfHelpers(): PdfHelpers {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
  const PW = PDF_PAGE_WIDTH_MM
  const PH = 297
  const M = PDF_MARGIN_MM
  const CW = PDF_CONTENT_WIDTH_MM
  const BOTTOM = PH - 18
  let y = M

  const lh = (fs: number) => fs * 0.42

  const checkBreak = (needed: number) => {
    if (y + needed > BOTTOM) {
      doc.addPage()
      y = M
    }
  }

  const txt: PdfHelpers['txt'] = (str, fs, style = 'normal', align = 'left', indent = 0, gap = 3) => {
    doc.setFont('helvetica', style)
    doc.setFontSize(fs)
    doc.setTextColor(0, 0, 0)
    const xPos = align === 'center' ? PW / 2 : align === 'right' ? PW - M : M + indent
    const maxW = align === 'center' ? CW : CW - indent
    const lines = doc.splitTextToSize(str, maxW)
    const h = lines.length * lh(fs)
    checkBreak(h + gap)
    doc.text(lines, xPos, y, { align })
    y += h + gap
  }

  const skip = (mm: number) => { y += mm }
  const hline = () => { doc.setDrawColor(0); doc.line(M, y, PW - M, y); skip(4) }

  function drawTable(config: {
    title: string;
    colHeaders: string[];
    rowHeaders: string[];
    data: number[][];
    highlightRow: number;
    highlightCol: number;
  }) {
    const { title, colHeaders, rowHeaders, data, highlightRow, highlightCol } = config;
    checkBreak(20 + (data.length + 1) * 6);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(0, 0, 0);
    doc.text(title, M, y);
    y += 5;

    const rowHeaderW = 22;
    const colW = (CW - rowHeaderW) / colHeaders.length;
    const rowH = 6;
    let cx = M;
    let cy = y;

    const drawColHeader = () => {
      let hx = M;
      doc.setFillColor(30, 41, 59);
      doc.setTextColor(255, 255, 255);
      doc.rect(hx, cy, rowHeaderW, rowH, 'F');
      hx += rowHeaderW;
      colHeaders.forEach(h => {
        doc.setFillColor(30, 41, 59);
        doc.rect(hx, cy, colW, rowH, 'F');
        doc.setFontSize(7);
        doc.text(String(h), hx + colW / 2, cy + rowH / 2 + 1, { align: 'center' });
        hx += colW;
      });
      cy += rowH;
    };

    // Encabezado de columnas
    drawColHeader();

    // Filas de datos
    data.forEach((row, ri) => {
      if (cy + rowH > BOTTOM) {
        doc.addPage();
        cy = M;
        drawColHeader();
      }
      cx = M;
      doc.setFillColor(30, 41, 59);
      doc.setTextColor(255, 255, 255);
      doc.rect(cx, cy, rowHeaderW, rowH, 'F');
      doc.setFontSize(7);
      doc.text(String(rowHeaders[ri]), cx + rowHeaderW / 2, cy + rowH / 2 + 1, { align: 'center' });
      cx += rowHeaderW;

      row.forEach((val, ci) => {
        const isHighlight = ri === highlightRow && ci === highlightCol;
        if (isHighlight) {
          doc.setFillColor(239, 68, 68);
          doc.setTextColor(255, 255, 255);
        } else {
          doc.setFillColor(255, 255, 255);
          doc.setTextColor(0, 0, 0);
        }
        doc.rect(cx, cy, colW, rowH, isHighlight ? 'F' : 'S');
        doc.setFont('helvetica', isHighlight ? 'bold' : 'normal');
        doc.text(String(val), cx + colW / 2, cy + rowH / 2 + 1, { align: 'center' });
        cx += colW;
      });
      cy += rowH;
    });

    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);
    y = cy + 4;
  }

  return { doc, txt, skip, hline, getY: () => y, checkBreak, drawTable }
}

export async function uploadAndRegisterPdf(
  companyId: string,
  folder: string,
  fileId: string,
  blob: Blob,
  filename: string,
  fields: { category: string; title: string; description: string; checklist_item_id: string; uploaded_by: string | null }
): Promise<string | null> {
  try {
    const path = `${companyId}/${folder}/${nombreSeguroStorage(`${fileId}.pdf`)}`
    let upErr = (await supabase.storage.from('sso-documents')
      .upload(path, blob, { upsert: false, contentType: 'application/pdf' })).error

    if (upErr) {
      const { error: updateErr } = await supabase.storage.from('sso-documents')
        .update(path, blob, { contentType: 'application/pdf' })
      if (updateErr) throw updateErr
    }

    const { data: pub } = supabase.storage.from('sso-documents').getPublicUrl(path)

    const { data: existingDoc } = await supabase
      .from('sso_documents')
      .select('id')
      .eq('company_id', companyId)
      .in('file_url', [pub.publicUrl, path])
      .maybeSingle()

    if (!existingDoc) {
      await supabase.from('sso_documents').insert({
        company_id: companyId,
        category: fields.category,
        title: fields.title,
        description: fields.description,
        file_url: pub.publicUrl,
        file_name: filename,
        uploaded_by: fields.uploaded_by,
        version: 1,
        is_current: true,
        checklist_item_id: fields.checklist_item_id,
      })
    }

    return pub.publicUrl
  } catch (err) {
    console.warn('No se pudo registrar el documento en sso_documents:', err)
    return null
  }
}

export async function generateFlowDiagramPDF(
  process: ProcessWithHierarchy,
  companyId: string,
  companyName: string,
  uploadedBy: string | null
): Promise<string | null> {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
  const PW = PDF_PAGE_WIDTH_MM
  const PH = 297
  const M = PDF_MARGIN_MM
  const CW = PDF_CONTENT_WIDTH_MM
  const BOTTOM = PH - 20
  const BOX_W = 130
  const BOX_X = M + (CW - BOX_W) / 2
  const CENTER_X = PW / 2
  let y = M

  const BLUE: [number, number, number] = [37, 99, 235]
  const SLATE: [number, number, number] = [100, 116, 139]
  const DARK: [number, number, number] = [15, 23, 42]

  const lh = (fs: number) => fs * 0.42

  const checkBreak = (needed: number) => {
    if (y + needed > BOTTOM) {
      doc.addPage()
      y = M
    }
  }

  const drawArrow = (cx: number, yTop: number, yBottom: number) => {
    doc.setDrawColor(...SLATE)
    doc.setLineWidth(0.5)
    doc.line(cx, yTop, cx, yBottom - 3)
    doc.setFillColor(...SLATE)
    doc.triangle(cx - 2, yBottom - 3, cx + 2, yBottom - 3, cx, yBottom, 'F')
  }

  const drawOval = (label: string) => {
    const h = 14
    checkBreak(h + 14)
    doc.setFillColor(...BLUE)
    doc.setDrawColor(...BLUE)
    doc.ellipse(CENTER_X, y + h / 2, BOX_W / 2, h / 2, 'FD')
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(11)
    doc.setTextColor(255, 255, 255)
    doc.text(label, CENTER_X, y + h / 2 + 3, { align: 'center' })
    const boxBottom = y + h
    y = boxBottom
    return boxBottom
  }

  const drawActivityBox = (label: string, taskNames: string[]) => {
    const padding = 5
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(11)
    const titleLines = doc.splitTextToSize(label, BOX_W - padding * 2)
    const titleH = titleLines.length * lh(11)

    doc.setFont('helvetica', 'normal')
    doc.setFontSize(8)
    let taskH = 0
    const wrappedTasks: string[][] = taskNames.map(t => {
      const lines = doc.splitTextToSize(`• ${t}`, BOX_W - padding * 2)
      taskH += lines.length * lh(8)
      return lines
    })

    const boxH = padding * 2 + titleH + (taskNames.length > 0 ? 3 + taskH : 0)
    checkBreak(boxH + 14)

    doc.setFillColor(255, 255, 255)
    doc.setDrawColor(...BLUE)
    doc.setLineWidth(0.5)
    doc.roundedRect(BOX_X, y, BOX_W, boxH, 3, 3, 'FD')

    let textY = y + padding + lh(11) * 0.75
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(11)
    doc.setTextColor(...DARK)
    titleLines.forEach((line: string) => {
      doc.text(line, BOX_X + padding, textY, { align: 'left' })
      textY += lh(11)
    })

    if (taskNames.length > 0) {
      textY += 2
      doc.setFont('helvetica', 'normal')
      doc.setFontSize(8)
      doc.setTextColor(...SLATE)
      wrappedTasks.forEach(lines => {
        lines.forEach((line: string) => {
          doc.text(line, BOX_X + padding, textY, { align: 'left' })
          textY += lh(8)
        })
      })
    }

    const boxBottom = y + boxH
    y = boxBottom
    return boxBottom
  }

  // ── Encabezado ──
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(14)
  doc.setTextColor(...DARK)
  doc.text(companyName, CENTER_X, y, { align: 'center' })
  y += 8
  doc.setFontSize(12)
  doc.text('Diagrama de Flujo de Procesos (GT-01)', CENTER_X, y, { align: 'center' })
  y += 8
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(11)
  doc.text(process.name, CENTER_X, y, { align: 'center' })
  y += 6
  if (process.description) {
    doc.setFont('helvetica', 'italic')
    doc.setFontSize(9)
    doc.setTextColor(...SLATE)
    const descLines = doc.splitTextToSize(process.description, CW)
    doc.text(descLines, CENTER_X, y, { align: 'center' })
    y += descLines.length * lh(9) + 4
  }
  doc.setDrawColor(...DARK)
  doc.line(M, y, PW - M, y)
  y += 10

  // ── Óvalo de inicio ──
  let prevBottom = drawOval('INICIO')
  y += 10

  // ── Cajas de actividad conectadas por flechas ──
  const activities = process.process_activities
  activities.forEach((a) => {
    drawArrow(CENTER_X, prevBottom, y)
    const taskNames = a.activity_tasks.map(t => t.name)
    prevBottom = drawActivityBox(a.name, taskNames)
    y = prevBottom + 10
  })

  // ── Óvalo de fin ──
  drawArrow(CENTER_X, prevBottom, y)
  drawOval('FIN')

  const blob = doc.output('blob')
  const filename = `GT01_${process.name.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`
  doc.save(filename)

  return uploadAndRegisterPdf(companyId, 'gestion-tecnica', `${process.id}-diagrama`, blob, filename, {
    category: 'procedimiento',
    title: `Diagrama de Flujo - ${process.name}`,
    description: 'Generado automáticamente desde el Constructor Inteligente de Gestión Técnica',
    checklist_item_id: 'GT-01',
    uploaded_by: uploadedBy,
  })
}

export async function generateJobDescriptionPDF(
  process: ProcessWithHierarchy,
  positionsFull: JobPositionLegalFields[],
  companyId: string,
  companyName: string,
  uploadedBy: string | null
): Promise<string | null> {
  const { doc, txt, skip, hline } = createPdfHelpers()

  txt(companyName, 14, 'bold', 'center', 0, 2)
  txt('Descriptivo de Puestos de Trabajo (GT-02)', 12, 'bold', 'center', 0, 6)
  txt(process.name, 11, 'bold', 'center', 0, 6)
  hline()
  skip(4)

  const seenPositionIds = new Set<string>()

  for (const activity of process.process_activities) {
    if (!activity.position_id || seenPositionIds.has(activity.position_id)) continue
    seenPositionIds.add(activity.position_id)
    const pos = positionsFull.find(p => p.id === activity.position_id)
    if (!pos) continue

    txt(pos.name, 12, 'bold', 'left', 0, 4)
    txt(`Número de trabajadores: ${pos.numero_trabajadores ?? '—'}`, 9, 'normal', 'left', 0, 2)
    txt(`Horas de jornada laboral: ${pos.horas_actividad_diarias ?? '—'}`, 9, 'normal', 'left', 0, 2)
    txt(`Actividades realizadas: ${pos.actividades_realizadas ?? '—'}`, 9, 'normal', 'left', 0, 2)
    txt(`Recursos utilizados: ${pos.recursos_utilizados ?? '—'}`, 9, 'normal', 'left', 0, 2)
    txt(`Materiales y agentes: ${pos.materiales_agentes ?? '—'}`, 9, 'normal', 'left', 0, 4)
    hline()
    skip(4)
  }

  if (seenPositionIds.size === 0) {
    txt('Ninguna actividad de este proceso tiene un puesto asignado todavía.', 10, 'italic', 'left', 0, 4)
  }

  const blob = doc.output('blob')
  const filename = `GT02_${process.name.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`
  doc.save(filename)

  return uploadAndRegisterPdf(companyId, 'gestion-tecnica', `${process.id}-descriptivo`, blob, filename, {
    category: 'procedimiento',
    title: `Descriptivo de Puestos - ${process.name}`,
    description: 'Generado automáticamente desde el Constructor Inteligente de Gestión Técnica',
    checklist_item_id: 'GT-02',
    uploaded_by: uploadedBy,
  })
}

export async function generateRiskMatrixPDF(
  process: ProcessWithHierarchy,
  companyId: string,
  companyName: string,
  uploadedBy: string | null
): Promise<string | null> {
  const { doc, txt, skip, hline } = createPdfHelpers()

  txt(companyName, 14, 'bold', 'center', 0, 2)
  txt('Matriz de Riesgos — Evaluación GTC-45 (GT-04)', 12, 'bold', 'center', 0, 6)
  txt(process.name, 11, 'bold', 'center', 0, 6)
  hline()
  skip(4)

  const taskIds = process.process_activities.flatMap(a => a.activity_tasks.map(t => t.id))
  const hazards = await fetchHazardsForTasks(taskIds)

  const taskLabel = new Map<string, { activity: string; task: string }>()
  process.process_activities.forEach(a => {
    a.activity_tasks.forEach(t => {
      taskLabel.set(t.id, { activity: a.name, task: t.name })
    })
  })

  if (hazards.length === 0) {
    txt('Ninguna actividad de este proceso tiene peligros identificados todavía.', 10, 'italic', 'left', 0, 4)
  } else {
    for (const h of hazards) {
      const label = taskLabel.get(h.task_id) ?? { activity: '', task: '' }
      const categoria = CATEGORIAS_PELIGRO.find(c => c.value === h.hazard_category)?.label ?? h.hazard_category
      const nivel = h.nivel_riesgo >= 600 ? 'I' : h.nivel_riesgo >= 150 ? 'II' : h.nivel_riesgo >= 40 ? 'III' : 'IV'

      txt(`${label.activity} — ${label.task}`, 11, 'bold', 'left', 0, 3)
      txt(`${categoria} — ${h.hazard_type}`, 9, 'normal', 'left', 0, 2)

      if (h.hazard_description) {
        txt(`Descripción: ${h.hazard_description}`, 9, 'normal', 'left', 0, 2)
      }
      if (h.possible_effects) {
        txt(`Efectos posibles: ${h.possible_effects}`, 9, 'normal', 'left', 0, 2)
      }

      txt(`ND: ${h.nivel_deficiencia} — NE: ${h.nivel_exposicion} — NP: ${h.nivel_probabilidad} — NC: ${h.nivel_consecuencia} — NR: ${h.nivel_riesgo}`, 9, 'normal', 'left', 0, 2)
      txt(`Interpretación: ${h.interpretacion_riesgo} — Aceptabilidad: ${h.aceptabilidad} (Nivel ${nivel})`, 9, 'bold', 'left', 0, 2)

      if (h.control_source) {
        txt(`Control fuente: ${h.control_source}`, 9, 'normal', 'left', 0, 2)
      }
      if (h.control_medium) {
        txt(`Control medio: ${h.control_medium}`, 9, 'normal', 'left', 0, 2)
      }
      if (h.control_individual) {
        txt(`Control individuo: ${h.control_individual}`, 9, 'normal', 'left', 0, 2)
      }

      if (h.medida_eliminacion) {
        txt(`Eliminación: ${h.medida_eliminacion}`, 9, 'normal', 'left', 0, 2)
      }
      if (h.medida_sustitucion) {
        txt(`Sustitución: ${h.medida_sustitucion}`, 9, 'normal', 'left', 0, 2)
      }
      if (h.medida_control_ingenieria) {
        txt(`Control ingeniería: ${h.medida_control_ingenieria}`, 9, 'normal', 'left', 0, 2)
      }
      if (h.medida_control_administrativo) {
        txt(`Control administrativo: ${h.medida_control_administrativo}`, 9, 'normal', 'left', 0, 2)
      }
      if (h.medida_epp) {
        txt(`EPP: ${h.medida_epp}`, 9, 'normal', 'left', 0, 4)
      }

      hline()
      skip(4)
    }
  }

  const blob = doc.output('blob')
  const filename = `GT04_${process.name.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`
  doc.save(filename)

  return uploadAndRegisterPdf(companyId, 'gestion-tecnica', `${process.id}-matriz-riesgos`, blob, filename, {
    category: 'procedimiento',
    title: `Matriz de Riesgos - ${process.name}`,
    description: 'Generado automáticamente desde el Constructor Inteligente de Gestión Técnica',
    checklist_item_id: 'GT-04',
    uploaded_by: uploadedBy,
  })
}

export async function generateRiskMatrixExcel(
  process: ProcessWithHierarchy,
  companyName: string
): Promise<void> {
  const taskIds = process.process_activities.flatMap(a => a.activity_tasks.map(t => t.id))
  const hazards = await fetchHazardsForTasks(taskIds)

  const taskLabel = new Map<string, { activity: string; task: string }>()
  process.process_activities.forEach(a => {
    a.activity_tasks.forEach(t => {
      taskLabel.set(t.id, { activity: a.name, task: t.name })
    })
  })

  const rows = hazards.map(h => {
    const label = taskLabel.get(h.task_id) ?? { activity: '', task: '' }
    return {
      Actividad: label.activity,
      Tarea: label.task,
      'Categoría': CATEGORIAS_PELIGRO.find(c => c.value === h.hazard_category)?.label ?? h.hazard_category,
      'Tipo de peligro': h.hazard_type,
      'Descripción': h.hazard_description ?? '',
      'Efectos posibles': h.possible_effects ?? '',
      'Control fuente': h.control_source ?? '',
      'Control medio': h.control_medium ?? '',
      'Control individuo': h.control_individual ?? '',
      ND: h.nivel_deficiencia,
      NE: h.nivel_exposicion,
      NP: h.nivel_probabilidad,
      NC: h.nivel_consecuencia,
      NR: h.nivel_riesgo,
      'Interpretación': h.interpretacion_riesgo,
      Aceptabilidad: h.aceptabilidad,
      'Eliminación': h.medida_eliminacion ?? '',
      'Sustitución': h.medida_sustitucion ?? '',
      'Control ingeniería': h.medida_control_ingenieria ?? '',
      'Control administrativo': h.medida_control_administrativo ?? '',
      EPP: h.medida_epp ?? '',
    }
  })

  const wb = XLSX.utils.book_new()
  const sheet = XLSX.utils.json_to_sheet(
    rows.length ? rows : [{ Actividad: 'Sin peligros identificados todavía' }]
  )

  if (rows.length > 0) {
    const FILL_BY_NIVEL: Record<string, string> = {
      'I':   'FFDC2626',
      'II':  'FFEA580C',
      'III': 'FFCA8A04',
      'IV':  'FF16A34A',
    }
    const headerKeys = Object.keys(rows[0])
    const aceptColIndex = headerKeys.indexOf('Aceptabilidad')
    const nrColIndex = headerKeys.indexOf('NR')

    if (aceptColIndex >= 0 && nrColIndex >= 0) {
      rows.forEach((row, rowIdx) => {
        const nr = row.NR
        const nivel = nr >= 600 ? 'I' : nr >= 150 ? 'II' : nr >= 40 ? 'III' : 'IV'
        const cellRef = XLSX.utils.encode_cell({ r: rowIdx + 1, c: aceptColIndex })
        const cell = sheet[cellRef]
        if (cell) {
          cell.s = {
            fill: { patternType: 'solid', fgColor: { rgb: FILL_BY_NIVEL[nivel] } },
            font: { color: { rgb: 'FFFFFFFF' }, bold: true },
          }
        }
      })
    }
  }

  XLSX.utils.book_append_sheet(wb, sheet, 'Matriz de Riesgos GTC-45')

  const filename = `GT04_${process.name.replace(/[^a-zA-Z0-9]/g, '_')}_${companyName.replace(/\s+/g, '_')}.xlsx`
  XLSX.writeFile(wb, filename, { cellStyles: true })
}
